"use client";

import Link from "next/link";
import { useFeedback } from "@/components/FeedbackButton";
import { tallySentence } from "@/lib/changelogCategories";
import type { DayCounts } from "@/lib/changelog";

/**
 * THE FEED'S RIGHT-HAND COLUMN — OSO-621.
 *
 * The feed row is capped at 1400px with a 288px sidebar on the left and a
 * centre that stops at `max-w-2xl`. On a wide screen that left roughly 400px of
 * nothing, which is not a design decision anybody made — it is what a two-column
 * layout looks like in a three-column space.
 *
 * Two quiet things go in it, and the emphasis matters: this column sits beside
 * the thing people came for. It is not a place to put a banner.
 */

/** "8. september" — the year is noise for "what changed lately". */
function formatDay(iso: string): string {
  const date = new Date(`${iso}T00:00:00`);
  if (Number.isNaN(date.getTime())) return iso;
  return new Intl.DateTimeFormat("da-DK", { day: "numeric", month: "long" }).format(date);
}

function LatestChangesCard({ days }: { days: DayCounts[] }) {
  return (
    <section
      aria-labelledby="nyt-paa-theta"
      className="bg-surface rounded-xl border border-border p-4"
    >
      <h2 id="nyt-paa-theta" className="text-sm font-semibold text-foreground">
        Nyt på Theta
      </h2>

      <ul className="mt-3 space-y-2">
        {days.map((d) => (
          <li key={d.date}>
            {/*
              STRAIGHT AT THE DAY, not at the top of /om: the card names a date,
              so the link has to land on that date's heading — `ChangelogDay`
              carries `id={date}` for exactly this.
            */}
            <Link
              href={`/om#${d.date}`}
              className="group block text-xs leading-relaxed text-muted-foreground hover:text-foreground"
            >
              <span className="font-medium text-foreground group-hover:underline">
                {formatDay(d.date)}
              </span>
              : {tallySentence(d.counts)}
            </Link>
          </li>
        ))}
      </ul>

      <Link
        href="/om"
        className="mt-3 inline-block text-xs font-medium text-primary hover:underline"
      >
        Se alle ændringer
      </Link>
    </section>
  );
}

function FeedbackCard() {
  const { openFeedback, feedbackAvailable } = useFeedback();
  if (!feedbackAvailable) return null;

  return (
    <section className="bg-surface rounded-xl border border-border p-4">
      <h2 className="text-sm font-semibold text-foreground">
        Hvad synes du om Theta?
      </h2>
      <p className="mt-1 text-xs leading-relaxed text-muted-foreground">
        Noget der driller, eller noget du savner? Vi læser det hele — og en del
        af det, der er lavet om for nylig, kom præcis den vej.
      </p>
      {/*
        OPENS THE DIALOG THAT ALREADY EXISTS, deliberately. There is a floating
        feedback button in the corner (`FeedbackButton`), and this is a second
        DOOR to it, not a second feature: two ways in that behaved even slightly
        differently would be worse than one.
      */}
      <button
        type="button"
        onClick={openFeedback}
        className="mt-3 text-xs font-medium text-primary hover:underline"
      >
        Send feedback
      </button>
    </section>
  );
}

/**
 * THE COLUMN ITSELF, not just its contents — so that an empty one takes no
 * room. `FeedbackCard` disappears for a signed-out reader and `latest` is null
 * on a checkout where the changelog is not beside `process.cwd()`; with the
 * `w-72` wrapper living in the page, that combination left 304px of dead rail
 * and a visibly off-centre feed.
 *
 * `min-[1340px]` is arithmetic rather than taste: 288 + 16 + 672 (`max-w-2xl`)
 * + 16 + 288 = 1280 of content plus the row's own `px-4`. At Tailwind's `xl`
 * the three columns do not fit and the middle one would be squeezed.
 */
export function FeedAside({ days }: { days: DayCounts[] }) {
  const { feedbackAvailable } = useFeedback();
  if (days.length === 0 && !feedbackAvailable) return null;

  return (
    <div className="hidden min-[1340px]:block w-72 shrink-0">
      <div className="sticky top-[4.5rem] space-y-4">
        {days.length > 0 && <LatestChangesCard days={days} />}
        <FeedbackCard />
      </div>
    </div>
  );
}
